import React, { useState, useEffect } from 'react';
import {
  Container, Typography, TextField, MenuItem, Button, Paper, Stack, Card, CardContent, CardActions,
  Checkbox, FormControlLabel, Alert, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, Collapse, Box, Link, Chip, Avatar
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useParams, useNavigate } from 'react-router-dom';
import { resolveSiteVariant } from '../../utils/siteVariant';
import { api } from '../../api';
import { getCurrentUser } from '../../services/auth';
import { TermsAcceptField, TERMS_VERSION } from '../../components/Terms';
import { validateName, validatePhone, validatePassword, validateEmail, PHONE_RULE_KEY } from '../../utils/validation';
import { pickServiceName } from '../../utils/serviceName';
import { formatMoney } from '../../utils/money';
import PasswordRequirements from '../../components/PasswordRequirements';
import { useResendTimer, formatMMSS } from '../../hooks/useResendTimer';
import { mediaUrl } from '../../utils/media';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

const initialsOf = (name) => (name || '').trim().split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase();

export default function BookByLink() {
  const { t, i18n } = useTranslation();
  const { remaining, start: startResend } = useResendTimer();
  const { hash } = useParams(); // /book/:hash
  // Чоловіча чи жіноча версія (фон/лого/підпис) — див. utils/siteVariant.js
  const [variant] = useState(resolveSiteVariant);
  const navigate = useNavigate();

  const [masters, setMasters] = useState([]);
  const [services, setServices] = useState([]);
  const [selectedMaster, setSelectedMaster] = useState('');
  const [selected, setSelected] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]); // часи для вибраної дати
  const [schedule, setSchedule] = useState(null); // { workdays[7], offDates[] }
  const [terms, setTerms] = useState(false); // прийняття правил
  const [termsInfo, setTermsInfo] = useState(null); // { text, version } правил майстра
  const [time, setTime] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [guestErrors, setGuestErrors] = useState({}); // { name, phone }
  const [error, setError] = useState(''); // помилки форми (інлайн)
  const [loadError, setLoadError] = useState(''); // фатальна помилка завантаження (повний екран)
  const [loading, setLoading] = useState(false);
  const [loadingInitial, setLoadingInitial] = useState(!!hash);
  const [currentUser, setCurrentUser] = useState(null); // залогінений клієнт

  // --- Промокод ---
  const [promoCode, setPromoCode] = useState('');
  const [promo, setPromo] = useState(null);      // { valid, code, discount, finalPrice, ... }
  const [promoErr, setPromoErr] = useState('');
  const [promoBusy, setPromoBusy] = useState(false);

  // Базова сума обраних послуг (для знижки й показу ціни)
  const baseSum = services
    .filter((s) => selected.includes(s.id))
    .reduce((acc, s) => acc + Number(s.price || 0), 0);

  // Хто зайшов — щоб не питати імʼя/телефон/код у зареєстрованого
  useEffect(() => {
    let ignore = false;
    getCurrentUser().then((u) => { if (!ignore) setCurrentUser(u); });
    return () => { ignore = true; };
  }, []);

  // --- OTP-верифікація ---
  const [otpOpen, setOtpOpen] = useState(false);
  const [otpStatus, setOtpStatus] = useState(''); // registered | guest | new
  const [otpDevCode, setOtpDevCode] = useState('');
  const [code, setCode] = useState('');
  const [otpError, setOtpError] = useState('');
  const [otpBusy, setOtpBusy] = useState(false);
  // опційне створення акаунта (для new/guest)
  const [wantAccount, setWantAccount] = useState(false);
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');

  // --- INIT: якщо є hash, отримати налаштування з бекенду ---
  useEffect(() => {
    async function fetchLinkData() {
      if (!hash) {
        setLoadingInitial(false);
        return;
      }
      try {
        // Дані лінка: { masterId, serviceId?, masterName }
        const linkData = await api.getBookingLink(hash);
        // Ім'я майстра беремо з лінка — без запиту до захищеного списку користувачів
        setMasters([{ id: linkData.masterId, name: linkData.masterName, avatarUrl: linkData.masterAvatar }]);

        setSelectedMaster(linkData.masterId);
        if (linkData.serviceId) {
          setSelected([Number(linkData.serviceId)]);
        }

        const res = await api.getServices(linkData.masterId);
        setServices(res);

        setLoadingInitial(false);
      } catch (e) {
        setLoadError(t('book.linkInvalid'));
        setLoadingInitial(false);
      }
    }
    fetchLinkData();
    // eslint-disable-next-line
  }, [hash]);

  // --- Якщо не hash: стандартний флоу (тільки якщо не завантажуємо через hash) ---
  useEffect(() => {
    if (hash) return;
    async function fetchData() {
      // Публічний список майстрів (лише id + ім'я)
      const mastersList = await api.getMasters();
      setMasters(mastersList);
      if (!selectedMaster && mastersList.length === 1) setSelectedMaster(mastersList[0].id);
    }
    fetchData();
    // eslint-disable-next-line
  }, [hash]);

  // --- Завантаження послуг для майстра ---
  useEffect(() => {
    async function fetchServices() {
      if (selectedMaster && (!hash || services.length === 0)) {
        const res = await api.getServices(selectedMaster);
        setServices(res);
      }
    }
    fetchServices();
    // eslint-disable-next-line
  }, [selectedMaster]);

  // --- Розклад майстра: щоб вимкнути неробочі дні в календарі ---
  useEffect(() => {
    async function fetchSchedule() {
      if (!selectedMaster) { setSchedule(null); return; }
      try {
        setSchedule(await api.getPublicSchedule(selectedMaster));
      } catch {
        setSchedule(null);
      }
    }
    fetchSchedule();
  }, [selectedMaster]);

  // Скидаємо вибір дати/часу при зміні майстра або послуг
  useEffect(() => {
    setSelectedDate(null);
    setAvailableSlots([]);
    setTime('');
    // Знижка рахується від суми послуг — при зміні послуг скидаємо застосований промокод
    setPromo(null);
    setPromoErr('');
  }, [selectedMaster, selected]);

  // Перевірити промокод (без консюмингу; показує знижку)
  const applyPromo = async () => {
    if (!promoCode.trim() || !selectedMaster || selected.length === 0) return;
    setPromoBusy(true);
    setPromoErr('');
    try {
      const r = await api.validatePromo(Number(selectedMaster), promoCode.trim(), baseSum);
      if (r && r.valid) setPromo(r);
      else { setPromo(null); setPromoErr(t('book.promoInvalid')); }
    } catch (e) {
      setPromo(null);
      setPromoErr(t('book.promoInvalid'));
    }
    setPromoBusy(false);
  };

  // Вільні слоти для вибраної дати — з серверного availability (єдине джерело)
  useEffect(() => {
    async function loadSlots() {
      setAvailableSlots([]);
      setTime('');
      if (!selectedMaster || selected.length === 0 || !selectedDate) return;
      try {
        const res = await api.getAvailability(
          Number(selectedMaster),
          selectedDate.format('YYYY-MM-DD'),
          selected
        );
        setAvailableSlots(res.slots || []);
      } catch (e) {
        setAvailableSlots([]);
      }
    }
    loadSlots();
    // eslint-disable-next-line
  }, [selectedDate, selectedMaster, selected]);

  // Крок 1: валідація + (гість) надсилання коду / (залогінений) прямий запис
  const handleCreate = async () => {
    setError('');
    if (!selectedMaster || selected.length === 0 || !selectedDate || !time) {
      setError(t('book.selectServiceDateTime'));
      return;
    }
    if (!terms) {
      setError(t('book.mustAcceptTerms'));
      return;
    }

    // Залогінений користувач — бронюємо одразу під його акаунт, без OTP
    if (currentUser) {
      setLoading(true);
      try {
        await api.createBooking({
          masterId: Number(selectedMaster),
          date: selectedDate.format('YYYY-MM-DD'),
          time,
          services: selected,
          promoCode: promo?.valid ? promoCode.trim() : undefined,
          termsAccepted: true,
          termsVersion: String(termsInfo?.version ?? TERMS_VERSION),
        });
        navigate('/user/cabinet');
      } catch (e) {
        setError(e.message || t('book.createError'));
      } finally {
        setLoading(false);
      }
      return;
    }

    // Гість — потрібні валідні імʼя/телефон і підтвердження кодом
    const gErrs = {};
    const nErr = validateName(name);
    const pErr = validatePhone(phone);
    if (nErr) gErrs.name = nErr;
    if (pErr) gErrs.phone = pErr;
    setGuestErrors(gErrs);
    if (Object.keys(gErrs).length > 0) {
      setError(t('book.checkNamePhone'));
      return;
    }
    setLoading(true);
    try {
      const res = await api.requestOtp(phone);
      setOtpStatus(res.status || '');
      setOtpDevCode(res.devCode || '');
      setCode('');
      setOtpError('');
      setWantAccount(false);
      setRegEmail('');
      setRegPassword('');
      setOtpOpen(true);
      startResend(res.retryAfter || 120);
    } catch (e) {
      setError(e.message || t('book.otpSendFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Повторно надіслати код
  const handleResend = async () => {
    if (remaining > 0) return; // кулдаун ще триває
    setOtpError('');
    try {
      const res = await api.requestOtp(phone);
      setOtpDevCode(res.devCode || '');
      startResend(res.retryAfter || 120);
    } catch (e) {
      setOtpError(e.message || t('book.otpResendFailed'));
    }
  };

  // Крок 2: підтвердити код -> (опційно створити акаунт) -> створити запис
  const handleVerifyAndBook = async () => {
    setOtpError('');
    if (!code || code.length < 4) {
      setOtpError(t('auth.enterSmsCode'));
      return;
    }
    setOtpBusy(true);
    try {
      const v = await api.verifyOtp({ phone, code });

      // Для нового номера — за бажанням одразу створюємо акаунт (email опційний)
      if (wantAccount && otpStatus !== 'registered') {
        const pwErr = validatePassword(regPassword);
        const emErr = validateEmail(regEmail);
        if (pwErr || emErr) {
          setOtpError(t(pwErr || emErr));
          setOtpBusy(false);
          return;
        }
        await api.register({
          name,
          phone,
          email: regEmail || undefined,
          password: regPassword,
          phoneToken: v.phoneToken,
        });
      }

      await api.createBooking({
        masterId: Number(selectedMaster),
        date: selectedDate.format('YYYY-MM-DD'),
        time,
        services: selected,
        guest: { name, phone },
        phoneToken: v.phoneToken,
        promoCode: promo?.valid ? promoCode.trim() : undefined,
        termsAccepted: true,
        termsVersion: String(termsInfo?.version ?? TERMS_VERSION),
      });
      setOtpOpen(false);
      navigate('/book-success');
    } catch (e) {
      setOtpError(e.message || t('book.confirmFailed'));
    } finally {
      setOtpBusy(false);
    }
  };

  // --- Відображення ---
  if (loadingInitial) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress color="primary" sx={{ mb: 2 }} />
          <Typography>{t('common.loading')}</Typography>
        </Paper>
      </Container>
    );
  }
  if (loadError) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Alert severity="error">{loadError}</Alert>
        </Paper>
      </Container>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      {/* Фон на весь екран, за контентом (fixed — не обмежений контейнером Layout) */}
      <Box sx={{
        position: 'fixed',
        inset: 0,
        zIndex: -1,
        ...variant.backgroundSx,
      }} />
      <Container maxWidth="sm" sx={{ pt: { xs: 1, md: 2 }, pb: { xs: 3, md: 5 } }}>
        {/* Брендовий заголовок Clean Hands Club */}
        <Box sx={{ textAlign: 'center', mb: 3, color: '#e7c983' }}>
          <Box component="img" src={variant.logo} alt="Clean Hands Club"
               sx={{ height: { xs: 62, md: 76 }, width: 'auto', mb: 1, filter: 'drop-shadow(0 4px 14px rgba(0,0,0,0.5))' }} />
          <Typography sx={{ fontWeight: 600, letterSpacing: '0.16em', textTransform: 'uppercase', fontSize: { xs: '1rem', md: '1.2rem' }, lineHeight: 1.2 }}>
            Clean Hands Club
          </Typography>
          <Typography sx={{ color: 'rgba(231,201,131,0.72)', letterSpacing: '0.26em', textTransform: 'uppercase', fontSize: '0.56rem', mt: 0.5 }}>
            {t(variant.taglineKey)}
          </Typography>
        </Box>
        <Paper sx={{ p: 3, bgcolor: 'rgba(250,247,242,0.96)', backdropFilter: 'blur(3px)', borderRadius: 3, boxShadow: '0 14px 44px rgba(0,0,0,0.5)' }}>
          <Typography variant="h5" gutterBottom>{t('book.title')}</Typography>

          {currentUser ? (
            <Alert severity="info" sx={{ my: 2 }}>
              {t('book.loggedInAs')} <b>{currentUser.name}</b> ({currentUser.phone}). {t('book.bookingInCabinet')}
            </Alert>
          ) : (
            <>
              <TextField
                label={t('common.name')}
                fullWidth
                value={name}
                onChange={e => { setName(e.target.value); setGuestErrors(p => ({ ...p, name: '' })); }}
                error={!!guestErrors.name} helperText={guestErrors.name ? t(guestErrors.name) : ' '}
                sx={{ mt: 2 }}
              />
              <TextField
                label={t('common.phone')}
                fullWidth
                value={phone}
                placeholder="+48601234567"
                onChange={e => { setPhone(e.target.value); setGuestErrors(p => ({ ...p, phone: '' })); }}
                error={!!guestErrors.phone} helperText={t(guestErrors.phone || PHONE_RULE_KEY)}
                sx={{ mb: 1 }}
              />
            </>
          )}

          {!hash && (
            <TextField
              label={t('book.master')}
              select
              fullWidth
              value={selectedMaster}
              onChange={e => { setSelectedMaster(e.target.value); setSelected([]); }}
              sx={{ mb: 2 }}
            >
              {masters.map(m => (
                <MenuItem key={m.id} value={m.id}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Avatar src={mediaUrl(m.avatarUrl)} sx={{ width: 28, height: 28, fontSize: 13 }}>
                      {initialsOf(m.name)}
                    </Avatar>
                    <span>{m.name}</span>
                  </Stack>
                </MenuItem>
              ))}
            </TextField>
          )}

          {/* Обраний майстер — кружок з фото */}
          {(() => {
            const m = masters.find(x => String(x.id) === String(selectedMaster));
            return m ? (
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                <Avatar src={mediaUrl(m.avatarUrl)} sx={{ width: 56, height: 56, fontSize: 20 }}>
                  {initialsOf(m.name)}
                </Avatar>
                <Typography variant="h6">{m.name}</Typography>
              </Stack>
            ) : null;
          })()}

          {services.length > 0 && (
            <>
              <Typography variant="body2" sx={{ mb: 1 }}>{t('book.services')}</Typography>
              <Stack spacing={2} mb={2}>
                {services.map(s => (
                  <Card
                    key={s.id}
                    sx={{
                      backgroundColor: selected.includes(s.id) ? 'primary.main' : 'background.default',
                      color: selected.includes(s.id) ? 'primary.contrastText' : 'text.primary',
                      borderRadius: 2,
                      boxShadow: selected.includes(s.id) ? 6 : 1,
                      transition: '0.2s'
                    }}
                  >
                    <CardContent>
                      <Typography variant="subtitle1" fontWeight="bold">{pickServiceName(s, i18n.language)}</Typography>
                      <Typography variant="body2">{t('book.duration', { duration: s.duration })}</Typography>
                      <Typography variant="body2">{t('book.price', { price: s.price ? formatMoney(s.price) : '—' })}</Typography>
                    </CardContent>
                    <CardActions>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selected.includes(s.id)}
                            onChange={() => setSelected(prev =>
                              prev.includes(s.id)
                                ? prev.filter(i => i !== s.id)
                                : [...prev, s.id])}
                            sx={{ color: 'inherit' }}
                          />
                        }
                        label={t('book.choose')}
                        disabled={!!(hash && services.length === 1)}
                      />
                    </CardActions>
                  </Card>
                ))}
              </Stack>
            </>
          )}

          {/* Промокод + підсумок ціни */}
          {selected.length > 0 && (
            <Box sx={{ mb: 2, p: 2, borderRadius: 2, bgcolor: '#f3ede2', border: '1px solid rgba(201,162,74,0.35)' }}>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <TextField
                  label={t('book.promoLabel')}
                  value={promoCode}
                  onChange={(e) => { setPromoCode(e.target.value.toUpperCase()); setPromo(null); setPromoErr(''); }}
                  size="small"
                  fullWidth
                />
                <Button variant="outlined" onClick={applyPromo} disabled={promoBusy || !promoCode.trim()}
                  sx={{ whiteSpace: 'nowrap', mt: 0.25 }}>
                  {t('book.promoApply')}
                </Button>
              </Stack>
              {promo?.valid && (
                <Alert severity="success" sx={{ mt: 1, py: 0 }}>{t('book.promoApplied', { code: promo.code })}</Alert>
              )}
              {promoErr && <Alert severity="error" sx={{ mt: 1, py: 0 }}>{promoErr}</Alert>}

              <Stack spacing={0.5} sx={{ mt: 1.5 }}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2">{t('book.priceSum')}</Typography>
                  <Typography variant="body2" sx={{
                    textDecoration: promo?.valid ? 'line-through' : 'none',
                    color: promo?.valid ? 'text.secondary' : 'text.primary',
                  }}>
                    {formatMoney(baseSum)}
                  </Typography>
                </Stack>
                {promo?.valid && (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" sx={{ color: '#5e6a4e' }}>{t('book.priceDiscount')}</Typography>
                    <Typography variant="body2" sx={{ color: '#5e6a4e' }}>−{formatMoney(promo.discount)}</Typography>
                  </Stack>
                )}
                <Stack direction="row" justifyContent="space-between">
                  <Typography sx={{ fontWeight: 700 }}>{t('book.priceFinal')}</Typography>
                  <Typography sx={{ fontWeight: 700 }}>
                    {formatMoney(promo?.valid ? promo.finalPrice : baseSum)}
                  </Typography>
                </Stack>
              </Stack>
            </Box>
          )}

          {selectedMaster && selected.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>{t('book.selectDate')}</Typography>
              <Box sx={{
                display: 'flex', justifyContent: 'center',
                '& .MuiPickerStaticWrapper-content': { minWidth: 'auto' },
              }}>
                <StaticDatePicker
                  displayStaticWrapperAs="desktop"
                  value={selectedDate}
                  onChange={d => { setSelectedDate(d); setTime(''); }}
                  disablePast
                  shouldDisableDate={(d) => {
                    if (!schedule) return false;
                    const ds = d.format('YYYY-MM-DD');
                    // Дата з «відкритими» годинами доступна навіть у неробочий день тижня
                    if (schedule.openDates && schedule.openDates.includes(ds)) return false;
                    if (schedule.workdays && schedule.workdays[d.day()] === false) return true;
                    if (schedule.offDates && schedule.offDates.includes(ds)) return true;
                    return false;
                  }}
                />
              </Box>
            </Box>
          )}

          {selectedDate && availableSlots.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>{t('book.time')}</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {availableSlots.map(t => (
                  <Chip
                    key={t}
                    label={t}
                    clickable
                    onClick={() => setTime(t)}
                    sx={{
                      bgcolor: time === t ? 'primary.main' : 'transparent',
                      color: time === t ? 'primary.contrastText' : 'text.primary',
                      border: '1px solid',
                      borderColor: time === t ? 'primary.main' : 'divider',
                      fontWeight: 500,
                      '&:hover': {
                        bgcolor: time === t ? 'primary.main' : 'action.hover',
                      },
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {selectedDate && availableSlots.length === 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>{t('book.noSlots')}</Alert>
          )}

          {selectedMaster && selected.length > 0 && time && (
            <Box sx={{ mb: 1 }}>
              <TermsAcceptField checked={terms} onChange={setTerms} masterId={selectedMaster} onTerms={setTermsInfo} />
            </Box>
          )}

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Stack direction="row" spacing={2}>
            <Button variant="contained" onClick={handleCreate} fullWidth disabled={loading}>
              {loading ? t('book.sendingCode') : t('book.book')}
            </Button>
          </Stack>
        </Paper>

        {/* --- Діалог підтвердження номера кодом (OTP) --- */}
        <Dialog open={otpOpen} onClose={() => !otpBusy && setOtpOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>{t('book.confirmPhone')}</DialogTitle>
          <DialogContent>
            {otpStatus === 'registered' ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                {t('book.registeredHint')}
              </Alert>
            ) : (
              <Typography variant="body2" sx={{ mb: 2 }} color="text.secondary">
                {t('book.codeSentPrefix')} <b>{phone}</b>. {t('book.codeSentSuffix')}
              </Typography>
            )}

            {otpDevCode && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {t('auth.devCode', { code: otpDevCode })}
              </Alert>
            )}

            <TextField
              label={t('auth.smsCode')}
              fullWidth
              value={code}
              onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputProps={{ inputMode: 'numeric' }}
              autoFocus
              sx={{ mb: 1 }}
            />

            <Link component="button" type="button" variant="body2" onClick={handleResend}
              disabled={otpBusy || remaining > 0}
              sx={{ color: remaining > 0 ? 'text.disabled' : undefined }}>
              {remaining > 0 ? t('auth.resendIn', { time: formatMMSS(remaining) }) : t('auth.resend')}
            </Link>

            {otpStatus !== 'registered' && (
              <Box sx={{ mt: 2 }}>
                <FormControlLabel
                  control={<Checkbox checked={wantAccount} onChange={e => setWantAccount(e.target.checked)} />}
                  label={t('book.createAccount')}
                />
                <Collapse in={wantAccount}>
                  <Stack spacing={1.5} sx={{ mt: 1 }}>
                    <TextField label={t('common.emailOptional')} type="email" fullWidth value={regEmail}
                      onChange={e => setRegEmail(e.target.value)} />
                    <TextField label={t('common.password')} type="password" fullWidth value={regPassword}
                      onChange={e => setRegPassword(e.target.value)} />
                    <PasswordRequirements value={regPassword} sx={{ px: 0.5 }} />
                  </Stack>
                </Collapse>
              </Box>
            )}

            {otpError && <Alert severity="error" sx={{ mt: 2 }}>{otpError}</Alert>}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setOtpOpen(false)} disabled={otpBusy}>{t('common.cancel')}</Button>
            <Button variant="contained" onClick={handleVerifyAndBook} disabled={otpBusy}>
              {otpBusy ? t('book.confirming') : t('book.confirmAndBook')}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </LocalizationProvider>
  );
}
